import { useCallback, useEffect, useState } from "react";
import { Users as UsersIcon, Store, CalendarCheck, FileClock, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Applications, Providers, Users, Bookings, Reviews, Categories } from "@/components/admin/AdminSections";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <Icon className={`h-5 w-5 ${accent || "text-sky-600"}`} />
    <p className="mt-3 font-heading text-3xl font-bold text-slate-900">{value ?? "-"}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export default function AdminDashboard() {
  useTitle("Admin Dashboard");
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)), []);
  useEffect(() => { loadStats(); }, [loadStats]);
  const tabs = [
    ["applications", `Applications${stats?.pending_applications ? ` (${stats.pending_applications})` : ""}`, <Applications onChange={loadStats} />],
    ["providers", "Providers", <Providers />],
    ["users", "Users", <Users />],
    ["bookings", "Bookings", <Bookings />],
    ["reviews", "Reviews", <Reviews onChange={loadStats} />],
    ["categories", "Categories", <Categories />],
  ];
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600">Administration</p>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-slate-900">Admin Dashboard</h1>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat icon={UsersIcon} label="Total customers" value={stats?.customers} testId="admin-stat-customers" />
        <Stat icon={Store} label="Live providers" value={stats?.providers} testId="admin-stat-providers" />
        <Stat icon={CalendarCheck} label="Total bookings" value={stats?.bookings} testId="admin-stat-bookings" />
        <Stat icon={FileClock} label="Pending applications" value={stats?.pending_applications} testId="admin-stat-pending" accent="text-amber-500" />
        <Stat icon={CheckCheck} label="Completed bookings" value={stats?.completed_bookings} testId="admin-stat-completed" accent="text-teal-600" />
      </div>
      <Tabs defaultValue="applications" className="mt-8">
        <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 no-scrollbar">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="h-10 rounded-lg px-4 text-sm font-semibold">{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k, , el]) => <TabsContent key={k} value={k} className="mt-6">{el}</TabsContent>)}
      </Tabs>
    </div>
  );
}
